import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signOut
} from 'firebase/auth';
import {
  addDoc,
  collection,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, firestore } from '../views/firebaseConfig';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

async function waitForServerDeletion(requestRef, onProgress) {
  let sawProcessing = false;

  for (let attempt = 0; attempt < 75; attempt += 1) {
    const snap = await getDoc(requestRef);

    if (!snap.exists()) {
      return;
    }

    const data = snap.data() || {};
    const status = data.status;
    const currentStep = data.currentStep || 'queued';

    onProgress({
      completed: Math.min(attempt + 1, 75),
      total: 75,
      status: status || 'PENDING',
      currentStep
    });

    if (status === 'COMPLETED') return;
    if (status === 'FAILED') {
      throw new Error(data.errorMessage || 'Pool 4 Real could not complete account deletion right now.');
    }
    if (status === 'PROCESSING' || status === 'FINALIZING') {
      sawProcessing = true;
    }

    await sleep(sawProcessing ? 1200 : 900);
  }

  throw new Error('The deletion request is still processing. Please try signing in later or contact rack@pool4real.com.');
}

export const deleteAccountAndData = async ({
  password,
  provider = 'password',
  onProgress = () => {},
  onError = () => {},
  onComplete = () => {}
}) => {
  const user = auth.currentUser;

  if (!user?.uid) {
    onError('You must be signed in before deleting your Pool 4 Real account.');
    return;
  }

  try {
    onProgress({ completed: 0, total: 75, status: 'REAUTHENTICATING', currentStep: 'reauthentication' });

    if (provider === 'google') {
      const credentialProvider = new GoogleAuthProvider();
      await reauthenticateWithPopup(user, credentialProvider);
    } else {
      if (!user.email || !password) {
        throw new Error('Enter your current password to confirm account deletion.');
      }
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    }

    onProgress({ completed: 1, total: 75, status: 'PENDING', currentStep: 'request-created' });

    const requestRef = await addDoc(collection(firestore, 'accountDeletionRequests'), {
      uid: user.uid,
      confirm: true,
      status: 'PENDING',
      platform: 'web',
      requestVersion: 2,
      requestedAt: serverTimestamp()
    });

    await waitForServerDeletion(requestRef, onProgress);
    await signOut(auth).catch(() => null);
    onComplete();
  } catch (error) {
    console.error('Pool 4 Real account deletion failed:', error);
    onError(error.message || 'Pool 4 Real could not complete account deletion right now.');
  }
};
